import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, Stethoscope, UserRound, Eye, Ear,
  HeartPulse, Mail, ShieldAlert,
} from "lucide-react";

const PERSONAS = [
  {
    to: "/mssante-medecin",
    label: "Médecin",
    desc: "Cabinet médical, CPTS, MSP — coordination ville-hôpital.",
    icon: Stethoscope,
    tag: "Métier",
  },
  {
    to: "/mssante-infirmier-liberal",
    label: "Infirmier libéral",
    desc: "IDEL en tournée — DMP mobile, coordination patients fragiles.",
    icon: UserRound,
    tag: "Métier",
  },
  {
    to: "/mssante/opticien",
    label: "Opticien-lunetier",
    desc: "Boîte organisationnelle pour magasins et centres de santé visuelle.",
    icon: Eye,
    tag: "Métier",
  },
  {
    to: "/mssante/audioprothesiste",
    label: "Audioprothésiste",
    desc: "Échanges sécurisés ORL ↔ patient ↔ assurance.",
    icon: Ear,
    tag: "Métier",
  },
  {
    to: "/mssante/professionnels-de-sante",
    label: "Tous les professionnels",
    desc: "Vue d'ensemble : médecins, paramédicaux, structures équipables.",
    icon: HeartPulse,
    tag: "Panorama",
  },
  {
    to: "/mssante/inscription",
    label: "Créer ma BAL MSSanté",
    desc: "Souscription en ligne — mise en service 24-48 h.",
    icon: Mail,
    tag: "Souscrire",
  },
  {
    to: "/lutte-fraude-sante",
    label: "Lutte fraude santé",
    desc: "Détection documentaire pour assureurs et complémentaires.",
    icon: ShieldAlert,
    tag: "Assureurs",
  },
];

/**
 * PersonaCardsGrid — Maillage interne SEO + entrée par métier.
 * Affiche un bandeau "Voir aussi par métier" avec 7 cards vers les pages persona MSSanté.
 * Booste le PageRank interne et donne au visiteur une porte d'entrée claire selon son profil.
 */
export default function PersonaCardsGrid({
  eyebrow = "Choisir par métier",
  title = "Trouvez la page faite pour vous.",
  subtitle = "Chaque métier de santé a ses spécificités. Découvrez l'offre MSSanté pensée pour votre quotidien.",
}) {
  return (
    <section className="relative py-20 lg:py-24 hex-soft" data-testid="section-personas">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="max-w-3xl mb-10">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#0284C7]">
            <span className="h-px w-7 bg-[var(--mc-blue-400)]" />
            {eyebrow}
          </div>
          <h2 className="font-display text-[#0F2B5B] text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mt-4">
            {title}
          </h2>
          {subtitle && (
            <p className="mt-4 text-[15px] text-[#64748B] leading-relaxed">
              {subtitle}
            </p>
          )}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PERSONAS.map(({ to, label, desc, icon: Icon, tag }) => (
            <Link
              key={to}
              to={to}
              data-testid={`persona-card-${to.replace(/\W+/g, "-")}`}
              className="group mc-card p-5 lg:p-6 flex flex-col gap-4 transition-all duration-300 hover:-translate-y-1 hover:border-[var(--mc-blue-400)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--mc-blue-400)]"
            >
              <div className="flex items-center justify-between">
                <div className="grid place-items-center h-10 w-10 rounded-lg bg-[rgba(45,123,255,0.12)] border border-[rgba(127,179,255,0.28)] text-[#0284C7] group-hover:bg-[rgba(45,123,255,0.22)] transition">
                  <Icon size={18} />
                </div>
                <span className="text-[10px] uppercase tracking-[0.18em] text-[#0284C7]">
                  {tag}
                </span>
              </div>
              <div>
                <h3 className="font-display text-[#0F2B5B] text-[16px] leading-snug">
                  {label}
                </h3>
                <p className="mt-2 text-[13px] text-[#64748B] leading-relaxed">
                  {desc}
                </p>
              </div>
              <div className="mt-auto pt-2 inline-flex items-center gap-1.5 text-[13px] text-[#0284C7] group-hover:text-[#0F2B5B] transition">
                <span>Voir la page</span>
                <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
